import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronLeft, Save, Building2, UserPlus, Copy, QrCode,
    Mail, User as UserIcon, MessageSquare, ShieldAlert,
    Globe, Smartphone
} from 'lucide-react';
import { SedeService, UserService } from '../services/apiService';
import { User, UserRole } from '../types';
import { useToast } from "../components/ToastContext";
import { useAuth } from "../contexts/AuthContext";

export const NuovaSedeView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { addToast } = useToast();
    const { refreshSedi } = useAuth();

    const [isLoading, setIsLoading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [inviteResult, setInviteResult] = useState<{link: string, sedeName: string} | null>(null);

    // Stato del form
    const [formData, setFormData] = useState({
        name: '',
        addressLine: '',
        city: '',
        zip: '',
        responsabileMode: 'EXISTING',
        responsabileId: '' as string | number,
        // Utenti che devono risultare responsabili della sede dopo il salvataggio;
        // il backend sincronizza per differenza.
        responsabiliIds: [] as number[],
        newResponsabileEmail: '',
        whatsappProvider: 'NONE',
        whatsappPhoneId: '',
        // I token sono write-only: il BE non li restituisce mai, manda solo i flag
        // `xxxSet`. Campo vuoto = non modificare.
        whatsappToken: '',
        whatsappTokenSet: false,
        twilioAccountSid: '',
        twilioAuthToken: '',
        twilioAuthTokenSet: false,
        twilioPhoneNumber: ''
    });

    useEffect(() => {
        loadUsers();
        if (id) {
            loadSede(id);
        }
    }, [id]);

    const loadUsers = async () => {
        try {
            const res = await UserService.getAll();
            if (res.success) setUsers(res.data);
        } catch (error) {
            console.error("Errore caricamento utenti", error);
        }
    };

    // Candidati responsabili: Responsabili esistenti e Collaboratori (promossi dal
    // backend al salvataggio). Esclusi i Titolari, che hanno già accesso pieno.
    const responsabiliCandidati = useMemo(
        () => users
            .filter(u => u.role !== UserRole.ADMIN)
            .sort((a, b) => {
                // Responsabili in cima, poi Collaboratori, dentro ciascun gruppo per nome.
                const rankA = a.role === UserRole.RESPONSABILE ? 0 : 1;
                const rankB = b.role === UserRole.RESPONSABILE ? 0 : 1;
                if (rankA !== rankB) return rankA - rankB;
                return (a.name || '').localeCompare(b.name || '');
            }),
        [users],
    );

    // Collaboratori selezionati che il BE promuoverà: mostriamo il badge
    // "Diventerà Responsabile" così il titolare lo sa in anticipo.
    const collaboratoriDaPromuovere = useMemo(
        () => responsabiliCandidati.filter(u =>
            u.role === UserRole.COLLABORATORE
            && formData.responsabiliIds.includes(Number(u.id)),
        ),
        [responsabiliCandidati, formData.responsabiliIds],
    );

    const loadSede = async (sedeId: string) => {
        setIsLoading(true);
        try {
            const res = await SedeService.getById(sedeId);
            if (res.success && res.data) {
                const s = res.data;
                setFormData(prev => ({
                    ...prev,
                    name: s.name,
                    addressLine: s.address || '',
                    city: s.citta || '',
                    zip: s.cap || '',
                    responsabileMode: 'EXISTING',
                    responsabileId: s.responsabileId || '',
                    // Precarica tutti i responsabili attuali, altrimenti salvando li rimuoveremmo.
                    responsabiliIds: Array.isArray((s as any).responsabili)
                        ? (s as any).responsabili.map((r: any) => Number(r.id)).filter((n: number) => !isNaN(n))
                        : (s.responsabileId ? [Number(s.responsabileId)] : []),
                    whatsappProvider: s.whatsappProvider || 'NONE',
                    whatsappPhoneId: s.whatsappPhoneId || '',
                    // I token non arrivano mai in GET: il form parte vuoto e i flag `Set`
                    // dicono se il valore è già presente sul server.
                    whatsappToken: '',
                    whatsappTokenSet: !!s.whatsappTokenSet,
                    twilioAccountSid: s.twilioAccountSid || '',
                    twilioAuthToken: '',
                    twilioAuthTokenSet: !!s.twilioAuthTokenSet,
                    twilioPhoneNumber: s.twilioPhoneNumber || ''
                }));
            } else {
                addToast("Errore caricamento sede", "error");
                navigate('/sedi');
            }
        } catch (error) {
            addToast("Errore di connessione", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const payload: any = {
                name: formData.name,
                address: formData.addressLine,
                citta: formData.city,
                cap: formData.zip,
                responsabileMode: formData.responsabileMode,
                newResponsabileEmail: formData.responsabileMode === 'NEW_INVITE' ? formData.newResponsabileEmail : undefined,
                // Se valorizzato, `responsabiliIds` prevale sul vecchio `responsabileId`.
                responsabiliIds: formData.responsabileMode === 'EXISTING' ? formData.responsabiliIds : undefined,
                responsabileId: formData.responsabileMode === 'EXISTING' && formData.responsabileId ? Number(formData.responsabileId) : undefined,
                whatsappProvider: formData.whatsappProvider,
                whatsappPhoneId: formData.whatsappPhoneId,
                whatsappToken: formData.whatsappToken,
                twilioAccountSid: formData.twilioAccountSid,
                twilioAuthToken: formData.twilioAuthToken,
                twilioPhoneNumber: formData.twilioPhoneNumber
            };

            if (id) {
                // Update
                const resUpdate = await SedeService.update(id, payload);
                if (resUpdate.success) {
                    await refreshSedi();
                    addToast("Sede aggiornata con successo", "success");
                    navigate('/sedi');
                } else {
                    addToast(resUpdate.message || "Errore aggiornamento", "error");
                }
            } else {
                // Create
                const resCreate = await SedeService.create(payload);

                await refreshSedi();

                if (resCreate.inviteLink) {
                    setInviteResult({
                        link: resCreate.inviteLink,
                        sedeName: (resCreate.sede && resCreate.sede.name) ? resCreate.sede.name : formData.name
                    });
                } else {
                    addToast("Sede creata con successo", "success");
                    navigate('/sedi');
                }
            }
        } catch (error: any) {
            const msg = error.response?.data?.message || "Errore durante il salvataggio";
            addToast(msg, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = () => {
        if(inviteResult?.link) {
            navigator.clipboard.writeText(inviteResult.link);
            addToast("Link copiato!", "success");
        }
    };

    // Modale invito generato
    if (inviteResult) {
        return (
            <div className="fixed inset-0 bg-gray-900/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center border-t-8 border-blue-600">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600">
                        <QrCode size={32}/>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Sede Creata!</h2>
                    <p className="text-gray-600 mb-6 text-sm">
                        La sede <strong>{inviteResult.sedeName}</strong> è attiva.<br/>
                        Invia questo link al nuovo responsabile per configurare l'accesso.
                    </p>
                    <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg mb-6 border border-gray-200">
                        <input type="text" readOnly value={inviteResult.link} className="bg-transparent flex-1 text-xs text-blue-600 font-mono outline-none truncate select-all"/>
                        <button onClick={copyToClipboard} className="text-gray-500 hover:text-gray-800 font-bold text-xs flex items-center gap-1 px-2 py-1 hover:bg-gray-200 rounded transition-colors">
                            <Copy size={12}/> Copia
                        </button>
                    </div>
                    <button onClick={() => navigate('/sedi')} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors">
                        Fatto, torna alla lista
                    </button>
                </div>
            </div>
        );
    }

    // Form principale
    return (
        <div className="px-4 lg:px-6 py-6 w-full max-w-6xl mx-auto pb-24">
            <div className="flex items-center gap-2 text-gray-500 hover:text-gray-900 cursor-pointer w-fit transition-colors mb-6" onClick={() => navigate('/sedi')}>
                <ChevronLeft size={20}/> Torna alla lista
            </div>

            <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                    <Building2 size={24}/>
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{id ? "Modifica Sede" : "Nuova Sede Operativa"}</h1>
                    <p className="text-sm text-gray-500">{id ? "Aggiorna i dati e la configurazione WhatsApp" : "Aggiungi una nuova filiale e assegna un responsabile"}</p>
                </div>
            </div>

            <form id="sede-form" onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4">
                        <h3 className="font-bold text-gray-900 border-b pb-2">Dati Logistici</h3>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nome Sede *</label>
                            <input required type="text" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Es. Sede Milano" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo *</label>
                            <input required type="text" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Via Roma, 10" value={formData.addressLine} onChange={e => setFormData({...formData, addressLine: e.target.value})} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                                <input type="text" className="w-full p-2.5 border border-gray-300 rounded-lg" placeholder="Milano" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                                <input type="text" className="w-full p-2.5 border border-gray-300 rounded-lg" placeholder="20100" value={formData.zip} onChange={e => setFormData({...formData, zip: e.target.value})} />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4">
                        <h3 className="font-bold text-gray-900 border-b pb-2">Responsabile Sede</h3>
                        {!id && (
                            <div className="flex gap-4 mb-4">
                                <button type="button" onClick={() => setFormData({...formData, responsabileMode: 'EXISTING'})} className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-all ${formData.responsabileMode === 'EXISTING' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Seleziona Esistente</button>
                            </div>
                        )}
                        {formData.responsabileMode === 'EXISTING' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Responsabili ({formData.responsabiliIds.length} selezionati)
                                </label>
                                <p className="text-[11px] text-gray-500 mb-2">
                                    Una sede può avere più responsabili. Seleziona uno o più utenti dalla lista. I Collaboratori scelti verranno promossi automaticamente al ruolo Responsabile al salvataggio.
                                </p>
                                {collaboratoriDaPromuovere.length > 0 && (
                                    <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-[12px] text-amber-800">
                                        <strong>Promozione di ruolo al salvataggio:</strong>{' '}
                                        {collaboratoriDaPromuovere.map(c => `${c.name} ${c.surname}`).join(', ')} diventerà Responsabile.
                                    </div>
                                )}
                                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-white divide-y divide-gray-100">
                                    {responsabiliCandidati.length === 0 ? (
                                        <div className="text-sm text-gray-500 px-3 py-4 text-center italic">
                                            Nessun utente disponibile.<br/>
                                            <span className="text-xs">Crea prima un Responsabile o un Collaboratore dalla pagina Utenti, oppure usa "Invita Nuovo" qui sotto.</span>
                                        </div>
                                    ) : (
                                        responsabiliCandidati.map(u => {
                                            const uid = Number(u.id);
                                            const checked = formData.responsabiliIds.includes(uid);
                                            const isCollab = u.role === UserRole.COLLABORATORE;
                                            return (
                                                <label
                                                    key={u.id}
                                                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors ${checked ? 'bg-blue-50/60' : ''}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={checked}
                                                        onChange={e => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                responsabiliIds: e.target.checked
                                                                    ? [...prev.responsabiliIds, uid]
                                                                    : prev.responsabiliIds.filter(x => x !== uid),
                                                            }));
                                                        }}
                                                    />
                                                    <UserIcon size={14} className="text-gray-400 shrink-0"/>
                                                    <span className="text-sm text-gray-700 flex-1 min-w-0 truncate">
                                                        <span className="font-medium">{u.name} {u.surname}</span>
                                                        <span className="text-gray-400 ml-1">({u.email})</span>
                                                    </span>
                                                    <span
                                                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shrink-0 ${
                                                            isCollab
                                                                ? 'bg-slate-100 text-slate-600 border border-slate-200'
                                                                : 'bg-blue-100 text-blue-700 border border-blue-200'
                                                        }`}
                                                        title={isCollab ? 'Verrà promosso a Responsabile al salvataggio se selezionato' : 'Ruolo attuale: Responsabile'}
                                                    >
                                                        {isCollab ? 'Collaboratore' : 'Responsabile'}
                                                    </span>
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                                <label className="block text-sm font-medium text-green-900 mb-1">Email Responsabile</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-green-600" size={18}/>
                                    <input type="email" required={formData.responsabileMode === 'NEW_INVITE'} className="w-full pl-10 p-2.5 border border-green-300 rounded-lg bg-white" placeholder="email@esempio.it" value={formData.newResponsabileEmail} onChange={e => setFormData({...formData, newResponsabileEmail: e.target.value})} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/*<div className="space-y-6">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 h-fit">
                        <div className="flex items-center gap-2 mb-6 border-b pb-4">
                            <MessageSquare className="text-green-600" size={24}/>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Integrazione WhatsApp</h3>
                                <p className="text-xs text-gray-500">Scegli il provider per l'invio dei messaggi.</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-6">
                            <button type="button" onClick={() => setFormData({...formData, whatsappProvider: 'NONE'})} className={`p-3 rounded-lg border text-sm font-medium transition-all ${formData.whatsappProvider === 'NONE' ? 'bg-gray-100 border-gray-400 text-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>Nessuno</button>
                            <button type="button" onClick={() => setFormData({...formData, whatsappProvider: 'META'})} className={`p-3 rounded-lg border text-sm font-medium transition-all flex flex-col items-center gap-1 ${formData.whatsappProvider === 'META' ? 'bg-green-50 border-green-500 text-green-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}><Globe size={16}/> Meta (Cloud API)</button>
                            <button type="button" onClick={() => setFormData({...formData, whatsappProvider: 'TWILIO'})} className={`p-3 rounded-lg border text-sm font-medium transition-all flex flex-col items-center gap-1 ${formData.whatsappProvider === 'TWILIO' ? 'bg-red-50 border-red-500 text-red-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}><Smartphone size={16}/> Twilio</button>
                        </div>
                        {formData.whatsappProvider === 'META' && (
                            <div className="space-y-4">
                                <div className="bg-green-50 p-3 rounded-lg border border-green-100 text-xs text-green-800 flex gap-2">
                                    <ShieldAlert size={16} className="shrink-0"/>
                                    <p>Richiede un numero registrato su Meta for Developers.</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number ID</label>
                                    <input type="text" className="w-full p-2.5 border rounded-lg font-mono text-sm" placeholder="Es. 104..." value={formData.whatsappPhoneId} onChange={e => setFormData({...formData, whatsappPhoneId: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-2">
                                        Access Token
                                        {formData.whatsappTokenSet && (
                                            <span className="text-[10px] font-medium bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded">🔒 Impostato</span>
                                        )}
                                    </label>
                                    <textarea rows={3} className="w-full p-2.5 border rounded-lg font-mono text-xs resize-none" placeholder={formData.whatsappTokenSet ? '••••••••  (lascia vuoto per non modificare)' : 'Es. EAAG...'} value={formData.whatsappToken} onChange={e => setFormData({...formData, whatsappToken: e.target.value})} />
                                </div>
                            </div>
                        )}
                        {formData.whatsappProvider === 'TWILIO' && (
                            <div className="space-y-4">
                                <div className="bg-red-50 p-3 rounded-lg border border-red-100 text-xs text-red-800 flex gap-2">
                                    <ShieldAlert size={16} className="shrink-0"/>
                                    <p>Usa le API di Twilio.</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Account SID</label>
                                    <input type="text" className="w-full p-2.5 border rounded-lg font-mono text-sm" placeholder="Es. AC..." value={formData.twilioAccountSid} onChange={e => setFormData({...formData, twilioAccountSid: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-2">
                                        Auth Token
                                        {formData.twilioAuthTokenSet && (
                                            <span className="text-[10px] font-medium bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded">🔒 Impostato</span>
                                        )}
                                    </label>
                                    <input type="password" className="w-full p-2.5 border rounded-lg font-mono text-sm" placeholder={formData.twilioAuthTokenSet ? '••••••••  (lascia vuoto per non modificare)' : 'Es. 1a2b...'} value={formData.twilioAuthToken} onChange={e => setFormData({...formData, twilioAuthToken: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Twilio Sender</label>
                                    <input type="text" className="w-full p-2.5 border rounded-lg font-mono text-sm" placeholder="Es. +1..." value={formData.twilioPhoneNumber} onChange={e => setFormData({...formData, twilioPhoneNumber: e.target.value})} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>*/}
            </form>
            <button type="submit" form="sede-form" disabled={isLoading} className="w-full bg-blue-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-blue-700 shadow-lg transition-all flex justify-center items-center gap-2 mt-8">
                {isLoading ? "Salvataggio..." : <><Save size={20}/> {id ? "Salva Modifiche" : "Crea Sede"}</>}
            </button>
        </div>
    );
};

export default NuovaSedeView;